// ============================================================================
// BP-09-04: Mentor Hooks
// BrightPath Community - Peer Mentoring React Query Hooks
// ============================================================================

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { mentorApi } from '../api';
import {
  Mentor,
  MentorMatch,
  MentorMatchesResponse,
  MentorSearchParams,
  MentorApplicationInput,
  UpdateMentorProfileInput,
  MatchRequestInput,
  MatchFeedbackInput,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const mentorKeys = {
  all: ['mentors'] as const,
  list: (params?: MentorSearchParams) => [...mentorKeys.all, 'list', params] as const,
  detail: (id: string) => [...mentorKeys.all, 'detail', id] as const,
  stats: () => [...mentorKeys.all, 'stats'] as const,
  myProfile: () => [...mentorKeys.all, 'my-profile'] as const,
  matches: () => [...mentorKeys.all, 'matches'] as const,
  myMatches: () => [...mentorKeys.matches(), 'mine'] as const,
};

// =============================================================================
// Mentor List Hooks
// =============================================================================

export function useMentors(params?: MentorSearchParams) {
  return useQuery({
    queryKey: mentorKeys.list(params),
    queryFn: () => mentorApi.getMentors(params),
  });
}

export function useInfiniteMentors(params?: Omit<MentorSearchParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: mentorKeys.list(params),
    queryFn: ({ pageParam = 1 }) => mentorApi.getMentors({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.total_pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });
}

export function useMentor(
  mentorId: string,
  options?: Omit<UseQueryOptions<Mentor>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: mentorKeys.detail(mentorId),
    queryFn: () => mentorApi.getMentor(mentorId),
    enabled: !!mentorId,
    ...options,
  });
}

export function useMentorStats() {
  return useQuery({
    queryKey: mentorKeys.stats(),
    queryFn: () => mentorApi.getMentorStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// My Mentor Profile Hooks
// =============================================================================

export function useMyMentorProfile() {
  return useQuery({
    queryKey: mentorKeys.myProfile(),
    queryFn: () => mentorApi.getMyMentorProfile(),
    retry: false,
  });
}

export function useApplyToBeMentor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MentorApplicationInput) => mentorApi.applyToBecomeMentor(data),
    onSuccess: (newMentor) => {
      queryClient.setQueryData(mentorKeys.myProfile(), newMentor);
      queryClient.invalidateQueries({ queryKey: mentorKeys.list() });
      queryClient.invalidateQueries({ queryKey: mentorKeys.stats() });
    },
  });
}

export function useUpdateMentorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateMentorProfileInput) => mentorApi.updateMentorProfile(data),
    onSuccess: (updatedMentor) => {
      queryClient.setQueryData(mentorKeys.myProfile(), updatedMentor);
      queryClient.setQueryData(mentorKeys.detail(updatedMentor.id), updatedMentor);
      queryClient.invalidateQueries({ queryKey: mentorKeys.list() });
    },
  });
}

export function useToggleAcceptingMatches() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accepting: boolean) => mentorApi.toggleAcceptingMatches(accepting),
    onMutate: async (accepting) => {
      await queryClient.cancelQueries({ queryKey: mentorKeys.myProfile() });
      const previous = queryClient.getQueryData<Mentor>(mentorKeys.myProfile());

      if (previous) {
        queryClient.setQueryData(mentorKeys.myProfile(), {
          ...previous,
          is_accepting_matches: accepting,
        });
      }

      return { previous };
    },
    onError: (err, _, context) => {
      if (context?.previous) {
        queryClient.setQueryData(mentorKeys.myProfile(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myProfile() });
      queryClient.invalidateQueries({ queryKey: mentorKeys.list() });
    },
  });
}

// =============================================================================
// Mentor Match Hooks
// =============================================================================

export function useMyMatches() {
  return useQuery({
    queryKey: mentorKeys.myMatches(),
    queryFn: () => mentorApi.getAllMyMatches(),
  });
}

export function useRequestMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MatchRequestInput) => mentorApi.requestMatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useAcceptMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matchId: string) => mentorApi.acceptMatch(matchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useDeclineMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matchId, reason }: { matchId: string; reason?: string }) =>
      mentorApi.declineMatch(matchId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useStartMentorship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matchId: string) => mentorApi.startMentorship(matchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useCompleteMentorship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matchId: string) => mentorApi.completeMentorship(matchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
      queryClient.invalidateQueries({ queryKey: mentorKeys.myProfile() });
    },
  });
}

export function useCancelMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matchId, reason }: { matchId: string; reason?: string }) =>
      mentorApi.cancelMatch(matchId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useLeaveFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matchId, data }: { matchId: string; data: MatchFeedbackInput }) =>
      mentorApi.leaveFeedback(matchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mentorKeys.myMatches() });
    },
  });
}

export function useSendMentorMessage() {
  return useMutation({
    mutationFn: ({ matchId, message }: { matchId: string; message: string }) =>
      mentorApi.sendMessage(matchId, message),
  });
}
